/**
 * 用户菜单权限 API
 *
 * 用于 safeclaw-web 前台根据登录用户的角色，查询其拥有的菜单/按钮权限，
 * 从而控制界面元素（如"发布技能"、"创建课程"）的可见性。
 *
 * 接口对照后端:
 *   GET /discourse/api/v1/user/menus — 获取当前用户的菜单树（按角色合并）
 *
 * 缓存策略: 按钮权限码缓存到 localStorage（safeclaw_permissions / safeclaw_menus），
 * 登录时拉取一次，登出时清除。
 */

import type { UserMenuItem } from "@zrt-ai-office/types";
import { apiFetch } from "./client";

// ── 缓存 key ────────────────────────────────────────────────

/** 按钮权限码集合（type=button 节点的 name 字段，如 btn:skill:publish） */
const PERMISSIONS_KEY = "safeclaw_permissions";
/** 完整菜单树缓存（调试/扩展用） */
const MENUS_KEY = "safeclaw_menus";

// ── 类型 ────────────────────────────────────────────────────

/** 本地缓存的角色 + 按钮权限结构 */
export interface CachedPermissions {
  /** 按钮权限码集合；super_admin 为 ["*"] */
  buttons: string[];
}

// ── 工具：从菜单树提取按钮权限名 ──────────────────────────

/** 递归提取菜单树中所有 type=button 节点的 name 字段 */
export function extractButtonNames(nodes: UserMenuItem[]): string[] {
  const names: string[] = [];
  for (const node of nodes) {
    if (node.type === "button" && node.name) names.push(node.name);
    if (node.children?.length) {
      names.push(...extractButtonNames(node.children));
    }
  }
  return names;
}

// ── API 调用 ────────────────────────────────────────────────

/**
 * 获取当前用户的菜单树（按角色合并）
 * GET /discourse/api/v1/user/menus
 *
 * 后端根据当前 token 对应的用户角色合并所有菜单/按钮权限返回。
 */
export async function fetchUserMenus(): Promise<UserMenuItem[]> {
  return apiFetch<{ menus: UserMenuItem[] }>(
    "/discourse/api/v1/user/menus",
  ).then((resp) => resp.menus ?? []);
}

// ── localStorage 读写 ───────────────────────────────────────

export function getCachedButtons(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(PERMISSIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as string[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function setCachedButtons(buttons: string[]): void {
  try {
    localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(buttons));
  } catch {
    // noop
  }
}

export function getCachedMenus(): UserMenuItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(MENUS_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as UserMenuItem[];
  } catch {
    return [];
  }
}

export function setCachedMenus(menus: UserMenuItem[]): void {
  try {
    localStorage.setItem(MENUS_KEY, JSON.stringify(menus));
  } catch {
    // noop
  }
}

/**
 * 清空权限相关缓存（登出时调用）
 */
export function clearPermissionsCache(): void {
  try {
    localStorage.removeItem(PERMISSIONS_KEY);
    localStorage.removeItem(MENUS_KEY);
  } catch {
    // noop
  }
}

// ── 高层：拉取并缓存按钮权限 ────────────────────────────────

/**
 * 拉取当前用户的菜单树（按角色合并），提取按钮权限码并写入 localStorage 缓存。
 *
 * 流程:
 * 1. GET /discourse/api/v1/user/menus → 获取菜单树
 * 2. 从菜单树提取按钮权限码
 * 3. 缓存到 localStorage
 *
 * 任意一步失败会向上抛出，调用方决定是否回退到缓存。
 *
 * @returns 缓存后的按钮权限
 */
export async function loadAndCachePermissions(): Promise<CachedPermissions> {
  const menus = await fetchUserMenus();
  const buttons = extractButtonNames(menus);
  setCachedButtons(buttons);
  setCachedMenus(menus);
  return { buttons };
}

// ── 权限判断工具 ─────────────────────────────────────────────

/**
 * 判断当前缓存中是否拥有某个按钮权限码。
 * 支持 super_admin 的 "*" 通配（全部允许）。
 */
export function hasButtonPermission(code: string): boolean {
  const buttons = getCachedButtons();
  if (buttons.includes("*")) return true;
  return buttons.includes(code);
}
